import moment from "moment";

import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PinFrequencyHelper } from "@/Common/Helpers/PinFrequencyHelper";
import { WalkmeHelper } from "@/Common/Helpers/WalkmeHelper";
import { DrugInteractionAlertsFilterOption } from "@/Common/Models/Employee/Enums/DrugInteractionAlertsFilterOption";
import { PrescriptionPrintType } from "@/Common/Models/Employee/Enums/Settings";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Locale } from "@/Common/Models/Localization/Locale";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PinFrequencyDocumentType } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { OrphanLabsClinician } from "@/Settings/Api/Dto/Response/OrphanLabsClinician";
import { SettingsStore } from "@/Settings/Stores/SettingsStore";

class SettingsHandler extends BaseOperationHandler {
    private SharedMessagesClinicianChangeTimeout: number;
    private SharedTasksClinicianChangeTimeout: number;
    private SharedExternalCommunicationsClinicianChangeTimeout: number;
    private sharedPortalCommunicationsClinicianChangeTimeout: number;

    public constructor() {
        super();

        this.RegisterOperation(LocalOperations.Settings.LoadSharingConfiguration, this.LoadSharingConfiguration);
        this.RegisterOperation(LocalOperations.Settings.LanguageChange, this.LanguageChange);
        this.RegisterOperation(LocalOperations.Settings.UnitChange, this.UnitChange);
        this.RegisterOperation(LocalOperations.Settings.ActiveSiteChange, this.activeSiteChange);
        this.RegisterOperation(LocalOperations.Settings.DocumentViewChange, this.documentViewChange);
        this.RegisterOperation(LocalOperations.Settings.ChangePharmacotherapeuticAssistant, this.ChangePharmacotherapeuticAssistant);
        this.RegisterOperation(LocalOperations.Settings.ChangeDefaultRefillNumber, this.ChangeDefaultRefillNumber);
        this.RegisterOperation(LocalOperations.Settings.ChangeAutoIncludeDiagnoses, this.ChangeAutoIncludeDiagnoses);
        this.RegisterOperation(LocalOperations.Settings.SaveRefillNumber, this.SaveRefillNumber);
        this.RegisterOperation(LocalOperations.Settings.SaveAutoResponder, this.SaveAutoResponder);
        this.RegisterOperation(LocalOperations.Settings.ClearAutoResponder, this.ClearAutoResponder);
        this.RegisterOperation(LocalOperations.Settings.ClearSignatureImage, this.ClearSignatureImage);
        this.RegisterOperation(LocalOperations.Settings.SharedMessagesClinicianChange, this.SharedMessagesClinicianChange);
        this.RegisterOperation(LocalOperations.Settings.SharedTasksClinicianChange, this.SharedTasksClinicianChange);
        this.RegisterOperation(LocalOperations.Settings.SharedExternalCommunicationClinicianChange, this.SharedExternalCommunicationsClinicianChange);
        this.RegisterOperation(LocalOperations.Settings.ShareLabResults, this.ShareLabResults);
        this.RegisterOperation(LocalOperations.Settings.SignatureUploadComplete, this.SaveSignatureImage);
        this.RegisterOperation(LocalOperations.Settings.LoadSignatureImage, this.LoadSignatureImage);
        this.RegisterOperation(LocalOperations.Settings.ChangePageOrientation, this.ChangePageOrientation);
        this.RegisterOperation(LocalOperations.Settings.ChangePrintingHeightWidth, this.ChangePrintingHeightWidth);
        this.RegisterOperation(LocalOperations.Settings.ChangePrescriptionPrinter, this.ChangePrescriptionPrinter);
        this.RegisterOperation(LocalOperations.Settings.ChangeAppointmentPrinter, this.ChangeAppointmentPrinter);
        this.RegisterOperation(LocalOperations.Settings.ChangeTagSelection, this.ChangeTagSelection);
        this.RegisterOperation(LocalOperations.Settings.OrphanLabsClinicianChange, this.OrphanLabsClinicianChange);
        this.RegisterOperation(LocalOperations.Settings.OrphanLabsFromChange, this.OrphanLabsFromChange);
        this.RegisterOperation(LocalOperations.Settings.OrphanLabsToChange, this.OrphanLabsToChange);
        this.RegisterOperation(LocalOperations.Settings.LoadOrphanLabsForwards, this.LoadOrphanLabsForwards);
        this.RegisterOperation(LocalOperations.Settings.AddClinicianToOrphanLabsList, this.AddClinicianToOrphanLabsList);
        this.RegisterOperation(LocalOperations.Settings.RemoveClinicianFromOrphanLabsList, this.RemoveClinicianFromOrphanLabsList);
        this.RegisterOperation(LocalOperations.Settings.SavePreferedFaxNumber, this.SavePreferedFaxNumber);
        this.RegisterOperation(LocalOperations.Settings.ShowAndCalculateMedicationDispensationQuantities, this.showAndCalculateMedicationDispensationQuantities);
        this.RegisterOperation(LocalOperations.Settings.ShowDiagnosisCode, this.showDiagnosisCode);
        this.RegisterOperation(LocalOperations.Settings.RefreshSettings, this.refreshSettings);
        this.RegisterOperation(LocalOperations.Settings.LoadEmployeeEmails, this.LoadEmployeeEmails);
        this.RegisterOperation(LocalOperations.Settings.SaveEmployeeEmails, this.SaveEmployeeEmails);
        this.RegisterOperation(LocalOperations.Settings.VerifyEmployeeEmail, this.VerifyEmailAddress);
        this.RegisterOperation(LocalOperations.Settings.ResendEmployeeEmailVerificationCode, this.ResendEmailVerificationCode);
        this.RegisterOperation(LocalOperations.Settings.ActiveDepartmentChange, this.activeDepartmentChange);
        this.RegisterOperation(LocalOperations.Settings.ChangeTelemedWindowOption, this.toggleTelemedWindowOption);
        this.RegisterOperation(LocalOperations.Settings.UpdateSignatureAndPinFrequency, this.updateSignatureAndPinFrequency);
        this.RegisterOperation(LocalOperations.Settings.ChangeDefaultLongTermMedicationDuration, this.changeDefaultLongTermMedicationDuration);
        this.RegisterOperation(LocalOperations.Settings.ChangeDefaultLongTermMedicationRefillNumber, this.changeDefaultLongTermMedicationRefillNumber);
        this.RegisterOperation(LocalOperations.Settings.UpdatePinFrequency, this.updatePinFrequency);
        this.RegisterOperation(LocalOperations.Settings.UpdateAuthenticatorCodeDeliveryMethod, this.updateAuthenticatorCodeDeliveryMethod);
        this.RegisterOperation(LocalOperations.Settings.ChangeShouldAttachMedicalSummaryWhenCreatingConsultationRequest, this.toggleShouldAttachMedicalSummaryWhenCreatingConsultationRequest);
        this.RegisterOperation(LocalOperations.Settings.ChangeShouldAttachMedicationSummaryWhenCreatingConsultationRequest, this.toggleShouldAttachMedicationSummaryWhenCreatingConsultationRequest);
        this.RegisterOperation(LocalOperations.Settings.ChangeDrugInteractionAlertsFilterOption, this.changeDrugInteractionAlertsFilterOption);
        this.RegisterOperation(LocalOperations.Settings.SharedPortalCommunicationClinicianChange, this.changeSharedPortalCommunicationsClinician);
        this.RegisterOperation(LocalOperations.Settings.UpdateAllowPatientsToInitiatePortalMessages, this.updateAllowPatientsToInitiatePortalMessages);
        this.RegisterOperation(LocalOperations.Settings.ChangeShowAlertsAndSpecialNeedsInPrintedSummary, this.updateShowAlertsAndSpecialNeedsInPrintedSummary);
        this.RegisterOperation(LocalOperations.Settings.ChangeShowPopupForNewAlertsAndSpecialNeeds, this.updateShowPopupForNewAlertsAndSpecialNeeds);
    }

    private LoadSignatureImage(request: LocalEvent, store: SettingsStore): void {
        store.action.LoadSignatureImage();
    }

    private SaveSignatureImage(request: LocalEvent, store: SettingsStore): void {
        const path = request.Get<string>("path");
        store.action.SaveSignatureImage(path);
    }

    private LoadSharingConfiguration(request: LocalEvent, store: SettingsStore): void {
        store.action.LoadSharingSettings();
    }

    private LanguageChange(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<KeyValuePair<string, string>>("value").Value;
        let culture = "";
        let langCode = "";
        let locale: Locale = null;

        switch (value) {
            case "fr":
                culture = "fr-CA";
                langCode = "fr";
                locale = Locale.fr;
                break;

            case "en":
                culture = "en-CA";
                langCode = "en";
                locale = Locale.en;
                break;

            case "es":
            case "es-MX":
                culture = "es-MX";
                langCode = "es-MX";
                locale = Locale.es;
                break;
        }
        if (locale && culture && langCode) {
            kendo.culture(culture);
            moment.locale(langCode, { week: { dow: 0, doy: 6 } });
            WalkmeHelper.changeLanguage(locale);
            cacheStore.RequestApplicationStringAsync(locale, () => {
                store.action.ChangeLanguage(langCode);
                sessionStore.locale = locale;
                store.EmitChangeToListeners();
            });
        }
    }

    private documentViewChange(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<KeyValuePair<string, string>>("value").Value;

        switch (value) {
            case "annotateView":
                store.action.changeDocumentView(true);
                break;

            case "popoutView":
                store.action.changeDocumentView(false);
                break;
        }
    }

    private UnitChange(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<KeyValuePair<string, string>>("value").Value;

        switch (value) {
            case "metric":
                sessionStore.useMetricSystemAsDefault = true;
                store.action.ChangeDefaultUnit();
                break;
            case "imperial":
                sessionStore.useMetricSystemAsDefault = false;
                store.action.ChangeDefaultUnit();
                break;

            default:
                break;
        }
    }

    private activeSiteChange(request: LocalEvent, store: SettingsStore): void {
        let selectedCode = request.Get<string>("selectedCode");
        store.configuration = store.configuration.set("ActiveSite", selectedCode);
        store.configuration = store.configuration.set("CostCentersList", cacheStore.getCostCenterBySiteId(selectedCode));
        store.action.changeSite(selectedCode);
    }

    private SavePreferedFaxNumber(request: LocalEvent, store: SettingsStore): void {
        let faxAccountNumberId = request.Get<number>("faxAccountNumberId");
        let faxAccounts = store.configuration.get("FaxAccounts");

        let updatedList = faxAccounts.map(x => {
            let faxes = x.FaxNumbers.map(y => {
                if (y.Id == faxAccountNumberId) {
                    y.IsPrefered = true;
                }
                else {
                    y.IsPrefered = false;
                }
                return y;
            });

            x.FaxNumbers = faxes;
            return x;
        });

        store.configuration = store.configuration.set("FaxAccounts", updatedList.toList());
        store.configuration = store.configuration.set("PreferedFaxAccountNumberId", faxAccountNumberId);

        store.action.SavePreferedFaxNumber(faxAccountNumberId);
    }

    private ChangePharmacotherapeuticAssistant(request: LocalEvent, store: SettingsStore): void {
        let checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ActivatePharmacotherapeuticAssistant: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("PharmacoTherapeuticActive", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private ChangeAutoIncludeDiagnoses(request: LocalEvent, store: SettingsStore): void {
        let checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                IsProblemVisibility: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("AreDiagnosticsInPatientSummary", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private ChangeDefaultRefillNumber(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<string>("value");
        store.uiLogic = store.uiLogic.set("ShowSaveRefillNumber", true);
        store.configuration = store.configuration.set("DefaultRefillNumber", +value);
    }

    private SaveRefillNumber(request: LocalEvent, store: SettingsStore): void {
        settingsStore.action.UpdateEmployeeSettings(
            {
                DefaultRefill: store.configuration.get("DefaultRefillNumber")
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.uiLogic = store.uiLogic.set("ShowSaveRefillNumber", false);
                settingsStore.EmitChangeToListeners();
                sessionStore.getCurrentEmployee().Model.DefaultRefill = store.configuration.get("DefaultRefillNumber");
            }
        );
    }

    private SaveAutoResponder(request: LocalEvent, store: SettingsStore): void {
        const from = request.Get<Date>("from");
        const to = request.Get<Date>("to");
        const note = request.Get<string>("note");
        store.configuration = store.configuration.set("IsInVacation", true);
        store.configuration = store.configuration.set("FromVacationDate", from);
        store.configuration = store.configuration.set("ToVacationDate", to);
        store.configuration = store.configuration.set("VacationNote", note);
        store.uiLogic = store.uiLogic.set("ShowAutoResponderForm", false);

        store.action.UpdateAutoResponders(true, from, to, note);
    }

    private ClearAutoResponder(request: LocalEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("ShowAutoResponderForm", false);
        store.configuration = store.configuration.set("IsAutoResponseActive", false);
        store.configuration = store.configuration.set("IsInVacation", false);
        store.configuration = store.configuration.set("FromVacationDate", null);
        store.configuration = store.configuration.set("ToVacationDate", null);
        store.configuration = store.configuration.set("VacationNote", "");

        store.action.UpdateAutoResponders(false, null, null, null);
    }

    private ClearSignatureImage(request: LocalEvent, store: SettingsStore): void {
        store.configuration = store.configuration.set("SignatureByteArray", "");
        sessionStore.signOnPrintAccess = false;
        store.action.RemoveSignatureFile();
    }

    private SharedMessagesClinicianChange(request: LocalEvent, store: SettingsStore): void {
        // throttle save to db
        if (this.SharedMessagesClinicianChangeTimeout != null) {
            clearTimeout(this.SharedMessagesClinicianChangeTimeout);
            this.SharedMessagesClinicianChangeTimeout = null;
        }

        let clinician = request.Get<PersonDropdownItem>("value");
        if (clinician == null || clinician.Item == null)
            return;

        let selectedClinicians = store.configuration.get("CliniciansForMessages").toArray();

        if (selectedClinicians.indexOf(clinician.Item) > -1)
            selectedClinicians = selectedClinicians.filter(x => x.ContactId != clinician.Item.ContactId);
        else
            selectedClinicians.push(clinician.Item);

        store.configuration = store.configuration.set("CliniciansForMessages", Immutable.List(selectedClinicians));

        // timeout to save to db 2 seconds
        this.SharedMessagesClinicianChangeTimeout = window.setTimeout(
            () => store.action.UpdateSharingSettings(selectedClinicians.map(x => x.ContactId)),
            2000
        );
    }

    private SharedTasksClinicianChange(request: LocalEvent, store: SettingsStore): void {
        if (this.SharedTasksClinicianChangeTimeout != null) {
            clearTimeout(this.SharedTasksClinicianChangeTimeout);
            this.SharedTasksClinicianChangeTimeout = null;
        }

        let clinician = request.Get<PersonDropdownItem>("value");
        if (clinician == null || clinician.Item == null)
            return;

        let selectedClinicians = store.configuration.get("CliniciansForTasks").toArray();

        if (selectedClinicians.indexOf(clinician.Item) > -1)
            selectedClinicians = selectedClinicians.filter(x => x.ContactId != clinician.Item.ContactId);
        else
            selectedClinicians.push(clinician.Item);

        store.configuration = store.configuration.set("CliniciansForTasks", Immutable.List(selectedClinicians));

        // timeout to save to db 2 seconds
        this.SharedTasksClinicianChangeTimeout = window.setTimeout(
            () => store.action.UpdateSharingSettings(null, selectedClinicians.map(x => x.ContactId)),
            2000
        );
    }

    private SharedExternalCommunicationsClinicianChange(request: LocalEvent, store: SettingsStore): void {
        if (this.SharedExternalCommunicationsClinicianChangeTimeout != null) {
            clearTimeout(this.SharedExternalCommunicationsClinicianChangeTimeout);
            this.SharedExternalCommunicationsClinicianChangeTimeout = null;
        }

        let clinician = request.Get<PersonDropdownItem>("value");
        if (clinician == null || clinician.Item == null)
            return;

        let selectedClinicians = store.configuration.get("CliniciansForExternalCommunications").toArray();

        if (selectedClinicians.indexOf(clinician.Item) > -1)
            selectedClinicians = selectedClinicians.filter(x => x.ContactId != clinician.Item.ContactId);
        else
            selectedClinicians.push(clinician.Item);

        store.configuration = store.configuration.set("CliniciansForExternalCommunications", Immutable.List(selectedClinicians));

        // timeout to save to db 2 seconds
        this.SharedExternalCommunicationsClinicianChangeTimeout = window.setTimeout(
            () => store.action.UpdateSharingSettings(null, null, selectedClinicians.map(x => x.ContactId)),
            2000
        );
    }

    private ShareLabResults(request: LocalEvent, store: SettingsStore): void {
        let checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ShareLabResults: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("AreResultsShared", checked);
                let currentUser = sessionStore.getCurrentEmployee().Model;
                currentUser.IsSharingLabs = checked;
                let emp = EmployeeUIModel.fromCompositeObject(currentUser);
                sessionStore.rebindCurrentEmployee(emp);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private ChangePageOrientation(request: LocalEvent, store: SettingsStore): void {
        const kvp = request.Get<KeyValuePair<string, string>>("value");
        const isPortrait = kvp.Value === "landscape";

        store.configuration = store.configuration.set("ArePrintLabelsPortrait", isPortrait);
        this.UpdatePrintingSettings(store);
    }

    private ChangePrintingHeightWidth(request: LocalEvent, store: SettingsStore): void {
        const kvp = request.Get<KeyValuePair<string, string>>("value");
        const largePrint = kvp.Value === "large";
        store.configuration = store.configuration.set("IsLargePrint", largePrint);
        this.UpdatePrintingSettings(store);
    }

    private ChangePrescriptionPrinter(request: LocalEvent, store: SettingsStore): void {
        let self = this;
        let kvp = request.Get<KeyValuePair<string, number>>("value");
        let prescriptionPrintType = PrescriptionPrintType.Regular;
        switch (kvp.Value) {
            case PrescriptionPrintType.Thermal:
                prescriptionPrintType = PrescriptionPrintType.Thermal;
                break;
            case PrescriptionPrintType.Mexico:
                prescriptionPrintType = PrescriptionPrintType.Mexico;
                break;
            default:
                break;
        }

        store.configuration = store.configuration.set("prescriptionPrintType", prescriptionPrintType);

        self.UpdatePrintingSettings(store);
    }

    private ChangeAppointmentPrinter(request: LocalEvent, store: SettingsStore): void {
        const kvp = request.Get<KeyValuePair<string, string>>("value");
        const confirmationNormal = kvp.Value === "regular_b";
        store.configuration = store.configuration.set("IsNormalConfirmationPrinter", confirmationNormal);
        this.UpdatePrintingSettings(store);
    }

    private UpdatePrintingSettings(store: SettingsStore): void {
        let currentUser = sessionStore.getCurrentEmployee().Model;
        // printing
        let arePrintLabelsPortrait = store.configuration.get("ArePrintLabelsPortrait");
        let isLargeLabelPrint = store.configuration.get("IsLargePrint");
        let isRegularPrinter = store.configuration.get("IsNormalPrescriptionPrinter");
        let prescriptionPrintType = store.configuration.get("prescriptionPrintType");
        let isRegularRDVConfirmPrinter = store.configuration.get("IsNormalConfirmationPrinter");
        currentUser.PrintingSetting = arePrintLabelsPortrait;
        currentUser.LargeLabelPrinting = isLargeLabelPrint;
        currentUser.IsRegularPrinter = isRegularPrinter;
        currentUser.PrescriptionPrintType = prescriptionPrintType;
        currentUser.IsRegularRDVConfirmPrinter = isRegularRDVConfirmPrinter;

        let emp = EmployeeUIModel.fromCompositeObject(currentUser);
        sessionStore.rebindCurrentEmployee(emp);

        store.action.UpdatePrintingSettings(arePrintLabelsPortrait, isLargeLabelPrint, isRegularPrinter, prescriptionPrintType, isRegularRDVConfirmPrinter);
    }

    private ChangeTagSelection(request: LocalEvent, store: SettingsStore): void {
        const selection = request.Get<number[]>("value");
        const cache = store.configuration.get("PdfTagsCache");

        let valuesToUpdate = cache
            .filter(item => selection.indexOf(item.Key) >= 0);

        let tagIds = valuesToUpdate.map(m => m.Key);
        let tagNames = valuesToUpdate.map(m => m.Value);
        store.configuration = store.configuration.set("PDFSearchKeywords", tagNames.toList());

        let mappedValuesToSend = valuesToUpdate.map(elem => elem.Value);
        store.configuration = store.configuration.set("SelectePDFTagIds", tagIds.toList());
        store.action.UpdatePdfTagsForEmployee(mappedValuesToSend.toArray());

        let currentUser = sessionStore.getCurrentEmployee().Model;
        // pdf
        currentUser.AllowedFormTags[0] = mappedValuesToSend.toArray().join("*");
        let emp = EmployeeUIModel.fromCompositeObject(currentUser);
        sessionStore.rebindCurrentEmployee(emp);
    }

    private OrphanLabsClinicianChange(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<string>("value");
        const name = request.Get<string>("name");
        store.configuration = store.configuration.set("ClinicianIdForOrphanLabs", value);
        store.configuration = store.configuration.set("ClinicianNameForOrphanLabs", name);
    }

    private OrphanLabsFromChange(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<Date>("value");
        store.configuration = store.configuration.set("ClinicianDateFromOrphanLabs", value);
    }

    private OrphanLabsToChange(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<Date>("value");
        store.configuration = store.configuration.set("ClinicianDateToOrphanLabs", value);
    }

    private LoadOrphanLabsForwards(request: LocalEvent, store: SettingsStore): void {
        const licenseDtos = sessionStore.getCurrentEmployee().getLicenseDtos(true);
        store.action.GetOrphanLabsForwards(licenseDtos);
    }

    private AddClinicianToOrphanLabsList(request: LocalEvent, store: SettingsStore): void {
        const id = store.configuration.get("ClinicianIdForOrphanLabs");
        const fromDate = store.configuration.get("ClinicianDateFromOrphanLabs");
        const toDate = store.configuration.get("ClinicianDateToOrphanLabs");

        store.configuration = store.configuration.set("ClinicianIdForOrphanLabs", "");
        store.configuration = store.configuration.set("ClinicianNameForOrphanLabs", "");
        store.configuration = store.configuration.set("ClinicianDateFromOrphanLabs", null);
        store.configuration = store.configuration.set("ClinicianDateToOrphanLabs", null);

        const labContextIds = cacheStore.GetSitesContextIdsCommaSeparated();
        store.action.SaveOrphanLabsClinician(id, labContextIds, fromDate, toDate);
    }

    private RemoveClinicianFromOrphanLabsList(request: LocalEvent, store: SettingsStore): void {
        const entry = request.Get<OrphanLabsClinician>("value");
        const list = store.configuration.get("ClinicianListForOrphanLabs").toArray().filter(c => c.Id !== entry.Id);
        store.configuration = store.configuration.set("ClinicianListForOrphanLabs", Immutable.List(list));
        store.action.RemoveOrphanLabClinician(entry.Id); // fire and forget
    }

    private showAndCalculateMedicationDispensationQuantities(request: LocalEvent, store: SettingsStore): void {
        const checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ShouldShowAndCalculateMedicationDispensationQuantities: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("ShouldShowAndCalculateMedicationDispensationQuantities", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private showDiagnosisCode(request: LocalEvent, store: SettingsStore): void {
        let checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ShowDiagnosisCode: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("ShowDiagnosisCode", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private refreshSettings(request: LocalEvent, store: SettingsStore): void {
        let currentUser = sessionStore.getCurrentEmployee().Model;

        store.configuration = store.configuration.set("AreResultsShared", currentUser.IsSharingLabs);
        store.configuration = store.configuration.set("AllowPatientsToInitiatePortalMessages", currentUser.AllowPatientsToInitiatePortalMessages);

        // printing
        store.configuration = store.configuration.set("ArePrintLabelsPortrait", currentUser.PrintingSetting);
        store.configuration = store.configuration.set("IsLargePrint", currentUser.LargeLabelPrinting);
        store.configuration = store.configuration.set("IsNormalPrescriptionPrinter", currentUser.IsRegularPrinter);
        store.configuration = store.configuration.set("prescriptionPrintType", currentUser.PrescriptionPrintType);
        store.configuration = store.configuration.set("IsNormalConfirmationPrinter", currentUser.IsRegularRDVConfirmPrinter);

        // pdf
        const currentTagList = (currentUser.AllowedFormTags != null && currentUser.AllowedFormTags.length > 0) ? currentUser.AllowedFormTags[0].split("*") : null;
        store.configuration = store.configuration.set("PDFSearchKeywords", Immutable.List(currentTagList));
    }

    private LoadEmployeeEmails(request: LocalEvent, store: SettingsStore): void {
        store.action.LoadEmployeeEmails();
    }

    private SaveEmployeeEmails(request: LocalEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("PwdConfirmationInProgress", true);

        let userId = sessionStore.userId;
        let password = request.Get<string>("password");
        let isNotifiedByEmail = request.Get<boolean>("isNotifiedByEmail");
        let emails = request.Get<Email[]>("emails");

        store.action.SaveEmployeeEmails(emails, isNotifiedByEmail, userId, password);
    }

    private VerifyEmailAddress(request: LocalEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("EmailVerificationInProcess", true);

        store.configuration = store.configuration.set("AddEmailsErrorMessage", "");
        store.configuration = store.configuration.set("EmailVerificationErrorMessage", "");

        let verificationCode = request.Get<string>("verificationCode");
        let contactPointId = request.Get<string>("contactPointId");
        let emailAddress = request.Get<string>("emailAddress");

        store.action.VerifyEmailAddress(emailAddress, contactPointId, verificationCode);
    }

    private ResendEmailVerificationCode(request: LocalEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("EmailVerificationInProcess", true);

        store.configuration = store.configuration.set("AddEmailsErrorMessage", "");
        store.configuration = store.configuration.set("EmailVerificationErrorMessage", "");

        let emailAddress = request.Get<string>("emailAddress");
        let contactPointId = request.Get<string>("contactPointId");

        store.action.ResendEmailVerificationCode(emailAddress, contactPointId);
    }

    private activeDepartmentChange(request: LocalEvent, store: SettingsStore): void {
        let selectedCode = request.Get<string>("selectedCode");
        let activeSiteId = store.configuration.get("ActiveSite");

        store.action.changeActiveDepartment(activeSiteId, selectedCode);
    }

    private toggleTelemedWindowOption(request: LocalEvent, store: SettingsStore): void {
        let checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ShowTelemedWindowInMylePopup: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("IsTelemedWindowInMyleWindow", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private changeDefaultLongTermMedicationDuration(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<number>("value");
        const longTermRefillValue = request.Get<number>("longTermRefillValue");

        settingsStore.action.UpdateEmployeeSettings(
            {
                LongTermMedicationDurationInDays: value ?? 0 // The request handler requires a non null value to update this specific field
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("LongTermMedicationDurationInDays", value);

                const currentEmployee = sessionStore.getCurrentEmployee();
                currentEmployee.Model.LongTermMedicationDurationInDays = value;

                if (longTermRefillValue) {
                    currentEmployee.Model.LongTermMedicationDefaultRefillNumber = longTermRefillValue;
                    store.configuration = store.configuration.set("LongTermMedicationDefaultRefillNumber", longTermRefillValue);
                }

                cacheStore.RebindSingleEmployeeToCache(currentEmployee.Model);

                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private changeDefaultLongTermMedicationRefillNumber(request: LocalEvent, store: SettingsStore): void {
        const value = request.Get<number>("value");
        settingsStore.action.UpdateEmployeeSettings(
            { LongTermMedicationDefaultRefillNumber: value ?? 0 }, // The request handler requires a non null value to update this specific field
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("LongTermMedicationDefaultRefillNumber", value);

                const currentEmployee = sessionStore.getCurrentEmployee();
                currentEmployee.Model.LongTermMedicationDefaultRefillNumber = value;
                cacheStore.RebindSingleEmployeeToCache(currentEmployee.Model);

                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private updateSignatureAndPinFrequency(request: LocalEvent, store: SettingsStore): void {
        const signatureBase64 = request.Get<string | undefined>("signatureBase64");
        const loginPinFrequency = request.Get<LoginPinFrequencyModel>("loginPinFrequency");
        const isSignatureRemoved = request.Get<boolean>("isSignatureRemoved");

        if (signatureBase64 || isSignatureRemoved)
            store.configuration = store.configuration.set("SignatureByteArray", signatureBase64);

        if (loginPinFrequency)
            store.configuration = store.configuration.set("LoginPinFrequency", loginPinFrequency);
    }

    private updatePinFrequency(request: LocalEvent, store: SettingsStore): void {
        const pinFrequencyDocumentType = request.Get<PinFrequencyDocumentType>("pinFrequencyDocumentType");
        const token = request.Get<string>("token");

        const model = { ...store.configuration.get("LoginPinFrequency") };
        PinFrequencyHelper.setFrequencyToken(model, pinFrequencyDocumentType, token);
        store.configuration = store.configuration.set("LoginPinFrequency", model);
    }

    private updateAuthenticatorCodeDeliveryMethod(request: LocalEvent, store: SettingsStore): void {
        const method = request.Get<AuthenticatorCodeDeliveryMethod>("method");
        store.configuration = store.configuration.set("SelectedAuthenticatorCodeDeliveryMethod", method);
    }

    private toggleShouldAttachMedicalSummaryWhenCreatingConsultationRequest(request: LocalEvent, store: SettingsStore): void {
        const checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ShouldAttachMedicalSummaryWhenCreatingConsultationRequest: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("ShouldAttachMedicalSummaryWhenCreatingConsultationRequest", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private toggleShouldAttachMedicationSummaryWhenCreatingConsultationRequest(request: LocalEvent, store: SettingsStore): void {
        const checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                ShouldAttachMedicationSummaryWhenCreatingConsultationRequest: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("ShouldAttachMedicationSummaryWhenCreatingConsultationRequest", checked);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private changeDrugInteractionAlertsFilterOption(request: LocalEvent, store: SettingsStore): void {
        let option = request.Get<DrugInteractionAlertsFilterOption>("option");
        settingsStore.action.UpdateEmployeeSettings(
            {
                DrugInteractionAlertsFilterOption: option
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("SelectedDrugInteractionAlertsFilterOption", option);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private changeSharedPortalCommunicationsClinician(request: LocalEvent, store: SettingsStore): void {
        if (this.sharedPortalCommunicationsClinicianChangeTimeout != null) {
            clearTimeout(this.sharedPortalCommunicationsClinicianChangeTimeout);
            this.sharedPortalCommunicationsClinicianChangeTimeout = null;
        }

        const clinician = request.Get<PersonDropdownItem>("value");
        if (clinician?.Item == null)
            return;

        let selectedClinicians = store.configuration.get("CliniciansForPortalCommunications").toArray();
        if (selectedClinicians.indexOf(clinician.Item) > -1)
            selectedClinicians = selectedClinicians.filter(p => p.ContactId !== clinician.Item.ContactId);
        else
            selectedClinicians.push(clinician.Item);

        store.configuration = store.configuration.set("CliniciansForPortalCommunications", Immutable.List(selectedClinicians));

        // timeout to save to db 2 seconds
        this.SharedExternalCommunicationsClinicianChangeTimeout = window.setTimeout(
            () => store.action.UpdateSharingSettings(null, null, null, selectedClinicians.map(p => p.ContactId)),
            2000
        );
    }

    private updateAllowPatientsToInitiatePortalMessages(request: LocalEvent, store: SettingsStore): void {
        let checked = request.Get<boolean>("checked");
        settingsStore.action.UpdateEmployeeSettings(
            {
                AllowPatientsToInitiatePortalMessages: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("AllowPatientsToInitiatePortalMessages", checked);
                const currentUser = sessionStore.getCurrentEmployee().Model;
                currentUser.AllowPatientsToInitiatePortalMessages = checked;
                const employee = EmployeeUIModel.fromCompositeObject(currentUser);
                sessionStore.rebindCurrentEmployee(employee);
                settingsStore.EmitChangeToListeners();
            }
        );
    }

    private updateShowAlertsAndSpecialNeedsInPrintedSummary(request: LocalEvent, store: SettingsStore): void {
        const checked = request.Get<boolean>("checked");
        store.action.UpdateEmployeeSettings(
            {
                ShowAlertsAndSpecialNeedsInPrintedSummary: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("ShowAlertsAndSpecialNeedsInPrintedSummary", checked);
                store.EmitChangeToListeners();
            }
        );
    }

    private updateShowPopupForNewAlertsAndSpecialNeeds(request: LocalEvent, store: SettingsStore): void {
        const checked = request.Get<boolean>("checked");
        store.action.UpdateEmployeeSettings(
            {
                ShowPopupForNewAlertsAndSpecialNeeds: checked
            },
            response => {
                if (!response.IsSuccess)
                    return;

                store.configuration = store.configuration.set("ShowPopupForNewAlertsAndSpecialNeeds", checked);
                store.EmitChangeToListeners();
            }
        );
    }
}

export { SettingsHandler };
